import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Pencil, Trash2, Download, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  adminLogin, adminMe, setToken, clearToken, getToken,
  adminListInsights, adminDeleteInsight,
  adminSubscribers, adminContacts, adminQuiz, adminLeadMagnets, adminExport, resolveImg,
} from "@/lib/api";
import BlogEditor from "@/components/admin/BlogEditor";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(email, password);
      setToken(res.token);
      onLogin(res.user);
    } catch {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f6f5f3] px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-3xl border border-[#eee] p-8 shadow-sm" data-testid="admin-login-form">
        <div className="flex items-baseline gap-1.5 mb-1">
          <span className="font-head text-xl font-semibold">Track</span>
          <span className="font-head text-xl font-semibold text-[#1a1a1a]">Digital</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#7C6CF0] mb-1" />
        </div>
        <p className="text-[#8a8a8a] text-sm mb-8">Admin dashboard</p>
        <label className="text-sm font-medium mb-1.5 block">Email</label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mb-4" data-testid="login-email" />
        <label className="text-sm font-medium mb-1.5 block">Password</label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mb-6" data-testid="login-password" />
        <Button type="submit" disabled={loading} className="w-full bg-[#7C6CF0] hover:bg-[#5F4FD6]" data-testid="login-submit">
          {loading && <Loader2 className="animate-spin mr-2" size={16} />} Sign in
        </Button>
      </form>
    </div>
  );
};

const DataTable = ({ columns, rows, empty }) => (
  <div className="overflow-x-auto rounded-2xl border border-[#eee] bg-white">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-[#eee] text-left text-[#8a8a8a]">
          {columns.map((c) => <th key={c.key} className="px-4 py-3 font-medium whitespace-nowrap">{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-[#a0a0a0]">{empty}</td></tr>
        ) : rows.map((r, i) => (
          <tr key={i} className="border-b border-[#f2f2f2] last:border-0">
            {columns.map((c) => <td key={c.key} className="px-4 py-3 align-top text-[#2b2b2b]">{c.render ? c.render(r) : (r[c.key] ?? "-")}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Dashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("blogs");
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [quiz, setQuiz] = useState([]);
  const [magnets, setMagnets] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadPosts = useCallback(async () => {
    try { setPosts(await adminListInsights()); } catch { toast.error("Could not load posts"); }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => {
    if (tab === "subscribers") adminSubscribers().then(setSubs).catch(() => {});
    if (tab === "enquiries") adminContacts().then(setContacts).catch(() => {});
    if (tab === "quiz") adminQuiz().then(setQuiz).catch(() => {});
    if (tab === "magnets") adminLeadMagnets().then(setMagnets).catch(() => {});
  }, [tab]);

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteInsight(post.id);
      toast.success("Post deleted");
      loadPosts();
    } catch { toast.error("Could not delete the post"); }
  };

  const exportCsv = async (dataset) => {
    try { await adminExport(dataset); } catch { toast.error("Export failed"); }
  };

  return (
    <div className="min-h-screen bg-[#f6f5f3]">
      <header className="bg-white border-b border-[#eee] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-1.5">
            <span className="font-head text-lg font-semibold">Track</span>
            <span className="font-head text-lg font-semibold text-[#1a1a1a]">Digital</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#7C6CF0] mb-1" />
            <span className="text-[#8a8a8a] text-sm ml-2">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="text-sm text-[#4a4a4a] hover:text-black inline-flex items-center gap-1.5"><ExternalLink size={15} /> View site</Link>
            <span className="text-sm text-[#8a8a8a] hidden sm:inline">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={onLogout} data-testid="logout-btn"><LogOut size={15} className="mr-1.5" /> Log out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="blogs" data-testid="tab-blogs">Blogs ({posts.length})</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz">Quiz</TabsTrigger>
            <TabsTrigger value="magnets" data-testid="tab-magnets">Lead Magnets</TabsTrigger>
          </TabsList>

          <TabsContent value="blogs">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-head text-2xl font-semibold">Blog posts</h2>
              <Button onClick={() => { setEditing(null); setEditorOpen(true); }} className="bg-[#7C6CF0] hover:bg-[#5F4FD6]" data-testid="new-post-btn"><Plus size={16} className="mr-1.5" /> New post</Button>
            </div>
            <div className="grid gap-3">
              {posts.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-[#eee] p-3 flex items-center gap-4" data-testid="admin-post-row">
                  <div className="w-20 h-14 rounded-lg overflow-hidden bg-[#fafafa] shrink-0">
                    {p.cover && <img src={resolveImg(p.cover)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{p.title}</p>
                    <p className="text-xs text-[#8a8a8a] mt-0.5">{p.category} · {fmtDate(p.published_at)}{p.featured ? " · Featured" : ""}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => { setEditing(p); setEditorOpen(true); }} data-testid="edit-post-btn"><Pencil size={14} /></Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(p)} className="text-red-600 hover:text-red-700" data-testid="delete-post-btn"><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="subscribers">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-head text-2xl font-semibold">Newsletter subscribers ({subs.length})</h2>
              <Button variant="outline" onClick={() => exportCsv("subscribers")} data-testid="export-subscribers"><Download size={16} className="mr-1.5" /> Export CSV</Button>
            </div>
            <DataTable
              columns={[{ key: "email", label: "Email" }, { key: "created_at", label: "Subscribed", render: (r) => fmtDate(r.created_at) }]}
              rows={subs} empty="No subscribers yet." />
          </TabsContent>

          <TabsContent value="enquiries">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-head text-2xl font-semibold">Enquiries ({contacts.length})</h2>
              <Button variant="outline" onClick={() => exportCsv("contacts")} data-testid="export-enquiries"><Download size={16} className="mr-1.5" /> Export CSV</Button>
            </div>
            <DataTable
              columns={[
                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "service", label: "Service" },
                { key: "message", label: "Message", render: (r) => <span className="block max-w-md whitespace-pre-wrap">{r.message}</span> },
              ]}
              rows={contacts} empty="No enquiries yet." />
          </TabsContent>

          <TabsContent value="quiz">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-head text-2xl font-semibold">AI Readiness Quiz ({quiz.length})</h2>
              <Button variant="outline" onClick={() => exportCsv("quiz")} data-testid="export-quiz"><Download size={16} className="mr-1.5" /> Export CSV</Button>
            </div>
            <DataTable
              columns={[
                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                { key: "name", label: "Name", render: (r) => r.name || "-" },
                { key: "email", label: "Email", render: (r) => r.email || "-" },
                { key: "score", label: "Score", render: (r) => `${r.total_score}/${r.max_score}` },
                { key: "band", label: "Band" },
              ]}
              rows={quiz} empty="No quiz submissions yet." />
          </TabsContent>

          <TabsContent value="magnets">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-head text-2xl font-semibold">Lead Magnet Downloads ({magnets.length})</h2>
              <Button variant="outline" onClick={() => exportCsv("lead-magnets")} data-testid="export-magnets"><Download size={16} className="mr-1.5" /> Export CSV</Button>
            </div>
            <DataTable
              columns={[
                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                { key: "name", label: "Name", render: (r) => r.name || "-" },
                { key: "email", label: "Email", render: (r) => r.email || "-" },
                { key: "title", label: "Magnet" },
              ]}
              rows={magnets} empty="No downloads yet. Share your lead magnet URL on social to start capturing leads." />
          </TabsContent>
        </Tabs>
      </main>

      <BlogEditor open={editorOpen} post={editing} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); loadPosts(); }} />
    </div>
  );
};

export default function Admin() {
  const [status, setStatus] = useState("loading"); // loading | out | in
  const [user, setUser] = useState(null);

  useEffect(() => {
    document.title = "Admin | Track Digital";
    if (!getToken()) { setStatus("out"); return; }
    adminMe().then((u) => { setUser(u); setStatus("in"); }).catch(() => { clearToken(); setStatus("out"); });
  }, []);

  const logout = () => { clearToken(); setUser(null); setStatus("out"); };

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center bg-[#f6f5f3]"><Loader2 className="animate-spin text-[#7C6CF0]" size={28} /></div>;
  if (status === "out") return <LoginView onLogin={(u) => { setUser(u); setStatus("in"); }} />;
  return <Dashboard user={user} onLogout={logout} />;
}
